// src/pages/Unsubscribe.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

function Unsubscribe() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading, found, notFound, unsubscribed, error
    const [subscriberInfo, setSubscriberInfo] = useState(null);
    const [isUnsubscribing, setIsUnsubscribing] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('notFound');
            return;
        }

        fetchSubscriberInfo();
    }, [token]);

    const fetchSubscriberInfo = async () => {
        try {
            const response = await fetch(`/api/newsletter/subscriber/${token}`);
            const result = await response.json();

            if (result.success) {
                setSubscriberInfo(result.data);
                if (!result.data.isActive) {
                    setStatus('unsubscribed');
                } else {
                    setStatus('found');
                }
            } else {
                setStatus('notFound');
            }
        } catch (error) {
            console.error('Error fetching subscriber info:', error);
            setStatus('error');
        }
    };

    const handleUnsubscribe = async () => {
        setIsUnsubscribing(true);
        setMessage('');

        try {
            const response = await fetch(`/api/newsletter/unsubscribe/${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            const result = await response.json();

            if (result.success) {
                setStatus('unsubscribed');
                setMessage(result.message);
            } else {
                setMessage(result.message || 'Failed to unsubscribe');
            }
        } catch (error) {
            console.error('Error unsubscribing:', error);
            setMessage('Failed to unsubscribe. Please try again.');
        } finally {
            setIsUnsubscribing(false);
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading...</h2>
                        <p className="text-gray-600">Verifying your unsubscribe request...</p>
                    </div>
                );

            case 'found':
                return (
                    <div className="text-center">
                        <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Unsubscribe from Newsletter</h2>

                        <div className="bg-gray-50 rounded-lg p-6 mb-6">
                            <p className="text-gray-700 mb-2">
                                <strong>Email:</strong> {subscriberInfo?.email}
                            </p>
                            <p className="text-gray-700 mb-4">
                                <strong>Subscribed:</strong> {new Date(subscriberInfo?.subscribedAt).toLocaleDateString()}
                            </p>

                            <div className="text-left">
                                <h4 className="font-semibold text-gray-800 mb-2">Current Preferences:</h4>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Product Updates: {subscriberInfo?.preferences?.productUpdates ? 'Yes' : 'No'}</li>
                                    <li>• Tech News: {subscriberInfo?.preferences?.techNews ? 'Yes' : 'No'}</li>
                                    <li>• Tips & Insights: {subscriberInfo?.preferences?.tips ? 'Yes' : 'No'}</li>
                                </ul>
                            </div>
                        </div>

                        <p className="text-gray-600 mb-6">
                            Are you sure you want to unsubscribe from all PostMgr newsletters?
                            You'll miss out on product updates, tips, and exclusive content.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={handleUnsubscribe}
                                disabled={isUnsubscribing}
                                className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
                            >
                                {isUnsubscribing ? (
                                    <span className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Unsubscribing...
                                    </span>
                                ) : (
                                    'Yes, Unsubscribe Me'
                                )}
                            </button>

                            <button
                                onClick={() => navigate('/')}
                                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-lg transition-colors"
                            >
                                Keep My Subscription
                            </button>
                        </div>

                        {message && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-600">{message}</p>
                            </div>
                        )}
                    </div>
                );

            case 'unsubscribed':
                return (
                    <div className="text-center">
                        <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Successfully Unsubscribed</h2>
                        <p className="text-gray-600 mb-6">
                            You have been unsubscribed from the PostMgr newsletter.
                            We're sorry to see you go!
                        </p>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                            <h3 className="font-semibold text-blue-800 mb-2">Want to stay connected?</h3>
                            <p className="text-blue-700 text-sm mb-4">
                                You can still follow us on social media or visit our website for updates.
                            </p>
                            <div className="flex justify-center space-x-4">
                                <a
                                    href="https://twitter.com/postmgr"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    Twitter
                                </a>
                                <a
                                    href="https://linkedin.com/company/postmgr"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    LinkedIn
                                </a>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => navigate('/')}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                            >
                                Visit PostMgr
                            </button>
                            <button
                                onClick={() => navigate('/contact')}
                                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-lg transition-colors"
                            >
                                Send Feedback
                            </button>
                        </div>

                        {message && (
                            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-green-600">{message}</p>
                            </div>
                        )}
                    </div>
                );

            case 'notFound':
                return (
                    <div className="text-center">
                        <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Unsubscribe Link</h2>
                        <p className="text-gray-600 mb-6">
                            The unsubscribe link you clicked is invalid or has expired.
                            This could happen if you've already unsubscribed or the link is corrupted.
                        </p>
                        <button
                            onClick={() => navigate('/contact')}
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                        >
                            Contact Support
                        </button>
                    </div>
                );

            case 'error':
            default:
                return (
                    <div className="text-center">
                        <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Something Went Wrong</h2>
                        <p className="text-gray-600 mb-6">
                            We encountered an error processing your request. Please try again or contact support.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={fetchSubscriberInfo}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => navigate('/contact')}
                                className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium rounded-lg transition-colors"
                            >
                                Contact Support
                            </button>
                        </div>
                    </div>
                );
        }
    };

    return (
        <>
            <SEO
                title="Unsubscribe from PostMgr Newsletter"
                description="Unsubscribe from PostMgr email notifications and newsletters."
                noindex={true}
            />

            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white shadow-sm rounded-lg p-8">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </>
    );
}

export default Unsubscribe;